/**
 * PrivateInspoPanel - host-only inspo per client.
 * Persists in provider_clients.private_inspo array. Shows when client returns via Acuity match.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Image as ImageIcon, Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ui/image-upload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrivateInspoPanel({ clientId }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [note, setNote] = useState("");

  const auth = useCallback(() => {
    const t = localStorage.getItem("session_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await axios.get(`${API}/provider/clients/${clientId}/private-inspo`,
        { headers: auth(), withCredentials: true });
      setItems(res.data?.private_inspo || []);
    } catch (e) { /* silent */ }
  }, [clientId, auth]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!imageUrl && !note) { toast.error("Add an image URL or a note"); return; }
    try {
      await axios.post(`${API}/provider/clients/${clientId}/private-inspo`,
        { image_url: imageUrl, note },
        { headers: auth(), withCredentials: true });
      setImageUrl(""); setNote(""); setAdding(false);
      toast.success("Private inspo added");
      load();
    } catch (e) { toast.error("Could not add: " + e.message); }
  };

  const remove = async (inspoId) => {
    if (!confirm("Remove this inspo?")) return;
    try {
      await axios.delete(`${API}/provider/clients/${clientId}/private-inspo/${inspoId}`,
        { headers: auth(), withCredentials: true });
      load();
    } catch (e) { toast.error("Remove failed"); }
  };

  return (
    <div className="space-y-2" data-testid="private-inspo-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Lock className="w-3 h-3 text-gray-500" />
          <h4 className="text-xs font-semibold">Private Inspo (host-only)</h4>
          <span className="text-[10px] text-gray-400">{items.length} items</span>
        </div>
        {!adding && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAdding(true)} data-testid="add-private-inspo-btn">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        )}
      </div>
      <p className="text-[10px] text-gray-400">
        Auto-shown to you when this client books again via Acuity (matched by email).
      </p>

      {adding && (
        <Card className="border-dashed">
          <CardContent className="p-3 space-y-2">
            <ImageUpload
              label="Inspo image"
              value={imageUrl}
              onChange={(url) => setImageUrl(url || "")}
              previewSize="medium"
              aspectRatio="square"
              description="Upload from your device. No URL pasting; this is a real upload."
            />
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Notes (allergy, lash preference, color rec, etc.)"
              className="text-xs min-h-[60px]"
              data-testid="private-inspo-note-input"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={add} data-testid="save-private-inspo-btn">Save</Button>
              <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setImageUrl(""); setNote(""); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {items.length === 0 && !adding ? (
        <p className="text-[10px] text-gray-400 italic">No private inspo yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.map((it) => (
            <Card key={it.inspo_id} className="hover:bg-gray-50">
              <CardContent className="p-2 flex gap-2">
                {it.image_url ? (
                  <img src={it.image_url} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center shrink-0">
                    <ImageIcon className="w-4 h-4 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] line-clamp-2">{it.note || <span className="text-gray-400">No note</span>}</p>
                  <p className="text-[9px] text-gray-400">{new Date(it.created_at).toLocaleDateString()}</p>
                </div>
                <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => remove(it.inspo_id)} data-testid={`del-private-inspo-${it.inspo_id}`}>
                  <Trash2 className="w-3 h-3 text-red-500" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
